import { Spinner } from "@fieldbook/ui/spinner"

import { createFileRoute, useParams } from "@tanstack/react-router"
import { Menu } from "lucide-react"

import { useStatus } from "@powersync/react"
import { useProjectData } from "@/contexts/ProjectData"
import { setActiveProject } from "@/hooks/useActiveProject"
import { useNetwork } from "@/hooks/useNetwork"
import { Project } from "@fieldbook/common/types"
import { ButtonLink } from "@fieldbook/ui/button-link"
import { useActiveNavTarget } from "@/hooks/useActiveNavTarget"
import { useAuth } from "@/hooks/useAuth"
import { useMyPlannedSiteTasks } from "@/hooks/useMyPlannedSiteTasks"
import { useTasksLastViewedAt } from "@/hooks/useTasksLastViewedAt"
import { DraftsLauncher } from "@/components/project/DraftsLauncher"
import { useSearchMode } from "@/contexts/SearchMode"
import { ProjectNewDataMenu } from "@/components/project/NewDataMenu"

export const Route = createFileRoute("/_private/projects/$id/")({
  onEnter: ({ params }) => {
    setActiveProject(params.id)
  },
  component: ProjectDetail,
  pendingComponent: () => (
    <div className="px-auto mx-auto mt-36">
      <Spinner size={"xl"} />
    </div>
  ),
})

type NewDataButtonProps = {
  projectData: Pick<Project, "id" | "site_types">
}

const NewDataButton = ({ projectData }: NewDataButtonProps) => {
  const [navTarget] = useActiveNavTarget()

  if (navTarget) return null

  return (
    <ProjectNewDataMenu
      projectId={projectData.id}
      siteTypes={projectData.site_types ?? []}
    />
  )
}

function ProjectDetail() {
  const { id } = useParams({ from: "/_private/projects/$id/" })
  const { user } = useAuth()
  const { data, isError, isInitialSync, isSyncStuck, isRefetching } =
    useProjectData()
  const { isOnline } = useNetwork()
  const syncStatus = useStatus()
  const { session: searchModeSession } = useSearchMode()
  // Only surfaced while waiting on the initial sync — that's when a download
  // error means the user is stuck looking at a spinner that will never
  // resolve. Cleared automatically on the next successful download.
  const syncDownloadError = isInitialSync ? syncStatus.downloadError : undefined
  const isOfflineInitialSync = isInitialSync && !isOnline

  if (searchModeSession) return null

  if (isError && !data && !isOfflineInitialSync)
    return (
      <div className="px-auto mx-auto mt-36 flex flex-col items-center text-center">
        <span className="text-2xl text-orange-600/80">
          Unable to load project data
        </span>
      </div>
    )

  return (
    <div>
      <DataSheetTriggerWithBadge projectId={id} />
      <DraftsLauncher projectId={id} ownerId={user?.id ?? null} />

      {isOfflineInitialSync && (
        <div className="absolute bottom-20 left-1/2 z-10 flex w-80 max-w-[90vw] -translate-x-1/2 flex-col gap-1 rounded-lg border border-orange-600/80 bg-white/90 p-3 dark:bg-black/80">
          <span className="text-lg text-orange-600/80">
            Unable to sync this project while offline
          </span>
          <span className="text-muted-foreground text-xs">
            Connect to the internet to download this project's data. Syncing
            will retry automatically.
          </span>
        </div>
      )}

      {!isOfflineInitialSync && syncDownloadError && (
        <div className="border-destructive absolute bottom-20 left-1/2 z-10 flex w-80 max-w-[90vw] -translate-x-1/2 flex-col gap-1 rounded-lg border bg-white/90 p-3 dark:bg-black/80">
          <span className="text-destructive text-lg">
            Unable to sync project data
          </span>
          <span className="text-muted-foreground break-words text-xs">
            {syncDownloadError.message}
          </span>
          <span className="text-muted-foreground text-xs">
            Syncing will retry automatically. If this persists, try Reset local
            data in Settings.
          </span>
        </div>
      )}

      {!isOfflineInitialSync && !syncDownloadError && isSyncStuck && (
        <div className="absolute bottom-20 left-1/2 z-10 flex w-80 max-w-[90vw] -translate-x-1/2 flex-col gap-1 rounded-lg border border-orange-600/80 bg-white/90 p-3 dark:bg-black/80">
          <span className="text-lg text-orange-600/80">
            Sync is taking longer than expected
          </span>
          <span className="text-muted-foreground text-xs">
            Project data hasn't finished syncing. If this persists, try Reset
            local data in Settings.
          </span>
        </div>
      )}

      {!isOfflineInitialSync &&
        !syncDownloadError &&
        !isSyncStuck &&
        ((isInitialSync && isOnline) || isRefetching) && (
          <div className="text-primary border-primary absolute bottom-20 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-lg border bg-white/50 p-2">
            <Spinner size={"large"} />
            <span className="text-lg">Syncing Project Data</span>
          </div>
        )}

      {!isInitialSync && (
        <NewDataButton projectData={data?.project ?? { id, site_types: [] }} />
      )}
    </div>
  )
}

// "Open data sheet" button with a tiny red badge when the user has any
// tasks they haven't viewed yet in this project. Gating on the local
// `tasksLastViewedAt` timestamp (rather than the assignment's
// server-side ack status) makes the badge persist exactly until the
// user opens My Tasks, regardless of background delivery, notification
// clicks, or the auto-ack write.
const DataSheetTriggerWithBadge = ({ projectId }: { projectId: string }) => {
  const { user } = useAuth()
  const { data: tasks } = useMyPlannedSiteTasks(projectId, user?.id ?? null)
  const lastViewedAt = useTasksLastViewedAt(user?.id ?? null, projectId)

  // Fall back to the epoch when the user has never opened the list;
  // every existing task then counts as new.
  const cutoff = lastViewedAt ?? ""
  const unviewedCount = (tasks ?? []).filter((t) => {
    // Open tasks have no assignment date; use the planned-site's
    // creation timestamp so they still surface as "new" on arrival.
    const eventAt = t.assigned_at ?? t.planned_at ?? ""
    return eventAt > cutoff
  }).length

  return (
    <ButtonLink
      to={"/projects/$id/data"}
      params={{ id: projectId }}
      mask={{ to: "/projects/$id" }}
      className="text-primary top-(--fieldbook-map-control-top) absolute left-4 z-10 rounded-lg bg-white/50 p-2"
      size={"icon"}
    >
      <span className="relative inline-flex">
        <Menu className="size-6" />
        {unviewedCount > 0 && (
          <span
            aria-label={`${unviewedCount} new task${unviewedCount === 1 ? "" : "s"}`}
            className="absolute -right-3 -top-3 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-none text-white"
          >
            {unviewedCount > 9 ? "9+" : unviewedCount}
          </span>
        )}
      </span>
    </ButtonLink>
  )
}
